<script setup lang="ts">
import { onMounted, ref, useTemplateRef } from 'vue';
import type { CompanyInfo } from '@/services/types';
import DemoAlbum from './DemoAlbum.vue';
import DemoSearch from './DemoSearch.vue';
import MapPanel from './MapPanel.vue';

/**
 * The showcase panel: a Semantic tab strip over the map, image album, a static
 * services tab, and company search.
 *
 * Converted from demopanel.vue. Two things changed structurally. The tab strip
 * is still Semantic's jQuery module, since the markup and styling depend on it,
 * but selecting a company now calls an exposed method on MapPanel rather than
 * reaching through `$refs` and casting to `any`. And DemoAlbum no longer needs a
 * tab-select event, because it has nothing to re-measure on becoming visible —
 * CSS grid already laid it out.
 */

const TAB_MAP = 'map';

const tabStrip = ref<HTMLElement | null>(null);
const mapPanel = useTemplateRef<InstanceType<typeof MapPanel>>('mapPanel');

onMounted(() => {
  if (tabStrip.value) {
    window.$(tabStrip.value).find('.item').tab();
  }
});

async function onCompanyClick(company: CompanyInfo): Promise<void> {
  if (!company.location) {
    return;
  }

  await mapPanel.value?.setCompanyMarker(company);

  if (tabStrip.value) {
    // Semantic requires the call on the items, not the container, or the tab
    // never gets the active class.
    window.$(tabStrip.value).find('.item').tab('change tab', TAB_MAP);
  }
}
</script>

<template>
  <div>
    <div ref="tabStrip" class="demopanel ui top attached stackable menu">
      <a class="active item" data-tab="map">Kart</a>
      <a class="item" data-tab="imagealbum">Bildehåndtering</a>
      <a class="item" data-tab="realestate">Eiendom &amp; Byggteknisk</a>
      <a class="item" data-tab="search">Søk</a>
    </div>

    <div class="demopanel ui bottom attached active tab segment" data-tab="map">
      <MapPanel ref="mapPanel" />
    </div>

    <div class="demopanel ui bottom attached tab segment" data-tab="imagealbum">
      <h3 class="ui header">Oversiktlig, rask og god bildebehandling</h3>
      <p>
        <i>
          Med bevaring av bildekvalitet og mulighet for alle kjernefunksjoner som klipp, rotering,
          zoom osv.
        </i>
      </p>
      <p>Klikk og velg for dynamisk sortering..</p>
      <DemoAlbum />
    </div>

    <div class="demopanel ui bottom attached tab segment" data-tab="realestate">
      <h3 class="ui header">Nøkkelinformasjon om eiendom</h3>
      <p>
        <i>
          Alltid oppdatert og korrekt informasjon ved registrering, beslutningsprosesser,
          kvalitetssikring, rapportering, med mere.
        </i>
      </p>
      <div class="ui two column stackable grid">
        <div class="row">
          <div class="nine wide column">
            <b>Typiske tjenester og muligheter:</b>
            <ul class="app-list no-pad">
              <li>
                <i class="check darkpurple icon" />
                Direkte tilknytning mot Norges offisielle eiendomsregister (matrikkel/grunnbok)
              </li>
              <li><i class="check darkpurple icon" /> Adresse- og stedssøk</li>
              <li><i class="check darkpurple icon" /> Oversikt i kart</li>
              <li>
                <i class="check darkpurple icon" /> Rapport til utskrift eller elektronisk utsending
              </li>
              <li><i class="check darkpurple icon" /> Dokumentasjon, kvalitetssikring</li>
            </ul>
          </div>
          <div class="seven wide column">
            <img src="/images/house-bw-sm.png" alt="Illustrasjon av bolig" width="210" />
          </div>
        </div>

        <h3 class="ui header no-top-margin">Byggteknisk databehandling</h3>
        <p>
          <i>
            Alltid oppdatert vareinformasjon. Byggteknisk kalkulasjonsprogram direkte i
            webløsningen med vare- og arbeidskostnader og omfattende varesøk.
          </i>
        </p>
        <div class="row no-top-padding">
          <div class="column">
            <b>Typiske tjenester og muligheter:</b>
            <ul class="app-list no-pad">
              <li><i class="check darkpurple icon" /> Kalkulasjonsprogram</li>
              <li>
                <i class="check darkpurple icon" /> Direkte oppslag mot NOBB sin omfattende
                varedatabase
              </li>
              <li>
                <i class="check darkpurple icon" /> Elektronisk varedokumentasjon direkte fra
                leverandørene
              </li>
              <li><i class="check darkpurple icon" /> Oppslag og søk mot prislister</li>
              <li><i class="check darkpurple icon" /> Bearbeidelse av pris- og varelister</li>
            </ul>
          </div>
          <div class="column">
            <img src="/images/constr-site1-bw-sm.png" alt="Illustrasjon av byggeplass" width="210" />
          </div>
        </div>
      </div>
    </div>

    <div class="demopanel ui bottom attached tab segment" data-tab="search">
      <DemoSearch @company-click="onCompanyClick" />
    </div>
  </div>
</template>
